import React, { useCallback, useEffect, useState } from 'react';
import { Type } from 'lucide-react';
import { customExecutiveMetricsService } from '../../services/customExecutiveMetricsService';

export default function ExecutiveDashboardBrandingSection({
  organizationId,
  organizationName,
  disabled,
}) {
  const [branding, setBranding] = useState({
    dashboardTitle: '',
    dashboardSubtitle: '',
    operationalDashboardTitle: '',
    operationalDashboardSubtitle: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadBranding = useCallback(async () => {
    if (!organizationId) return;
    const data = await customExecutiveMetricsService.getLayoutOverrides(organizationId, {
      includeHistory: false,
    });
    setBranding({
      dashboardTitle: data.branding?.dashboardTitle || '',
      dashboardSubtitle: data.branding?.dashboardSubtitle || '',
      operationalDashboardTitle: data.branding?.operationalDashboardTitle || '',
      operationalDashboardSubtitle: data.branding?.operationalDashboardSubtitle || '',
    });
  }, [organizationId]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const handleSave = async () => {
    if (!organizationId) return;
    setSaving(true);
    setMessage('');
    try {
      const existing = await customExecutiveMetricsService.getLayoutOverrides(organizationId);
      await customExecutiveMetricsService.saveLayoutOverrides(organizationId, null, {
        ...(existing.branding || {}),
        dashboardTitle: branding.dashboardTitle.trim(),
        dashboardSubtitle: branding.dashboardSubtitle.trim(),
        operationalDashboardTitle: branding.operationalDashboardTitle.trim(),
        operationalDashboardSubtitle: branding.operationalDashboardSubtitle.trim(),
      });
      setMessage('Dashboard branding saved.');
    } catch (e) {
      setMessage(e.message || 'Failed to save branding.');
    } finally {
      setSaving(false);
    }
  };

  const defaultTitle = organizationName || 'Your organization name';
  const defaultSubtitle = 'Executive KPI Dashboard';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Type className="w-5 h-5 text-blue-600" />
          Executive dashboard branding
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Customize titles and subtitles for Executive and Operational dashboard headers. Leave blank
          to use your organization name automatically. Finance View uses its own title.
        </p>
      </div>

      {disabled && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Select an organization to customize dashboard branding.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Executive dashboard title</label>
          <input
            type="text"
            value={branding.dashboardTitle}
            onChange={(e) => setBranding((b) => ({ ...b, dashboardTitle: e.target.value }))}
            placeholder={defaultTitle}
            disabled={disabled || saving}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Default: {defaultTitle}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Executive dashboard subtitle</label>
          <input
            type="text"
            value={branding.dashboardSubtitle}
            onChange={(e) => setBranding((b) => ({ ...b, dashboardSubtitle: e.target.value }))}
            placeholder={defaultSubtitle}
            disabled={disabled || saving}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Default: {defaultSubtitle}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operational dashboard title</label>
          <input
            type="text"
            value={branding.operationalDashboardTitle}
            onChange={(e) => setBranding((b) => ({ ...b, operationalDashboardTitle: e.target.value }))}
            placeholder={defaultTitle}
            disabled={disabled || saving}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Leave blank to use organization name</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operational dashboard subtitle</label>
          <input
            type="text"
            value={branding.operationalDashboardSubtitle}
            onChange={(e) => setBranding((b) => ({ ...b, operationalDashboardSubtitle: e.target.value }))}
            placeholder="Work queues, service health, and frontline RCM metrics"
            disabled={disabled || saving}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Shown under the operational header title</p>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={disabled || saving}
        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save branding'}
      </button>
      {message && (
        <p className={`text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-700'}`}>
          {message}
        </p>
      )}
    </div>
  );
}
